'use client';

import { useEffect } from 'react';
import { getConfig } from '@/utils/config/config';
import { ddRumAction, ddRumError } from '@/utils/datadog';
import { PRODUCT_THROTTLES } from '@/utils/feature-flags/constants';
import type { XtremepushConfig } from '@/types/config';
import { useStore } from '@/store/store';

type NotificationsManager = (typeof import('@fanduel/um-notifications'))['UmNotifications'];

let notificationsManager: NotificationsManager | null = null;
let initializationPromise: Promise<NotificationsManager | null> | null = null;

export const isXtremepushConfigValid = (
    config?: XtremepushConfig
): config is XtremepushConfig & { apiKey: string; baseUrl: string } => Boolean(config?.apiKey && config?.baseUrl);

export const shouldUseXpNotifications = (featureFlags: Record<string, true>, config?: XtremepushConfig): boolean =>
    isXtremepushConfigValid(config) && (config?.enabled === true || Boolean(featureFlags[PRODUCT_THROTTLES.USE_XP]));

const initializeNotifications = async (config: XtremepushConfig): Promise<NotificationsManager | null> => {
    if (notificationsManager) {
        return notificationsManager;
    }

    if (initializationPromise) {
        return initializationPromise;
    }

    initializationPromise = (async () => {
        try {
            const [{ UmNotifications }, { initializeUnifiedModules }] = await Promise.all([
                import('@fanduel/um-notifications'),
                import('@/logic/unified-modules')
            ]);

            await initializeUnifiedModules();
            await UmNotifications.initialize({
                apiKey: config.apiKey,
                baseUrl: config.baseUrl,
                enableLogging: config.enableLogging ?? false,
                enableDefaultInbox: config.enableDefaultInbox ?? false,
                enableInAppMessages: config.enableInAppMessages ?? false
            });

            notificationsManager = UmNotifications;
            ddRumAction({
                name: 'UM_NOTIFICATIONS_INITIALIZATION_FINISHED',
                description: 'UM Notifications initialization completed.',
                use_case: 'NOTIFICATION'
            });
            return notificationsManager;
        } catch (error) {
            ddRumError({
                name: 'UM_NOTIFICATIONS_INITIALIZATION_ERROR',
                description: 'UM Notifications initialization failed.',
                use_case: 'NOTIFICATION',
                context: {
                    message: error instanceof Error ? error.message : String(error)
                },
                stack: error instanceof Error ? error : undefined
            });
            return null;
        } finally {
            initializationPromise = null;
        }
    })();

    return initializationPromise;
};

const setNotificationsUser = async (manager: NotificationsManager, userId: string): Promise<boolean> => {
    if (!userId) {
        return false;
    }

    try {
        await manager.setUser(userId);
        ddRumAction({
            name: 'UM_NOTIFICATIONS_USER_ASSOCIATED',
            description: `UM Notifications user associated with ID: ${userId}`,
            use_case: 'NOTIFICATION'
        });
        return true;
    } catch (error) {
        ddRumError({
            name: 'UM_NOTIFICATIONS_SET_USER_ERROR',
            description: 'UM Notifications user association failed.',
            use_case: 'NOTIFICATION',
            context: {
                message: error instanceof Error ? error.message : String(error)
            },
            stack: error instanceof Error ? error : undefined
        });
        return false;
    }
};

const clearNotificationsUser = async (manager: NotificationsManager): Promise<void> => {
    try {
        await manager.setUser('');
        ddRumAction({
            name: 'UM_NOTIFICATIONS_USER_CLEARED',
            description: 'UM Notifications user association cleared.',
            use_case: 'NOTIFICATION'
        });
    } catch (error) {
        ddRumError({
            name: 'UM_NOTIFICATIONS_CLEAR_USER_ERROR',
            description: 'UM Notifications user association failed to clear.',
            use_case: 'NOTIFICATION',
            context: {
                message: error instanceof Error ? error.message : String(error)
            },
            stack: error instanceof Error ? error : undefined
        });
    }
};

export const openNotificationsInbox = async (config: XtremepushConfig, userId: string) => {
    const manager = await initializeNotifications(config);
    if (!manager) {
        ddRumAction({
            name: 'UM_NOTIFICATIONS_INBOX_OPEN_SKIPPED',
            description: 'UM Notifications inbox opening skipped because initialization did not return a manager.',
            use_case: 'NOTIFICATION',
            context: { reason: 'manager_unavailable' }
        });
        return;
    }

    const isUserAssociated = await setNotificationsUser(manager, userId);
    if (!isUserAssociated) {
        return;
    }

    try {
        await manager.openInbox();
        ddRumAction({
            name: 'UM_NOTIFICATIONS_INBOX_OPENED',
            description: 'UM Notifications inbox opened.',
            use_case: 'NOTIFICATION'
        });
    } catch (error) {
        ddRumError({
            name: 'UM_NOTIFICATIONS_INBOX_OPEN_ERROR',
            description: 'UM Notifications inbox failed to open.',
            use_case: 'NOTIFICATION',
            context: {
                message: error instanceof Error ? error.message : String(error)
            },
            stack: error instanceof Error ? error : undefined
        });
    }
};

export const useNotificationsUm = (featureFlags: Record<string, true>) => {
    const config = getConfig().services.xtremepush;
    const userId = useStore((state) => state.userContext.accountSettings.accountID);
    const xpRequested = config?.enabled === true || Boolean(featureFlags[PRODUCT_THROTTLES.USE_XP]);
    const enabled = shouldUseXpNotifications(featureFlags, config);
    useEffect(() => {
        if (!enabled) {
            if (xpRequested && !isXtremepushConfigValid(config)) {
                ddRumError({
                    name: 'UM_NOTIFICATIONS_CONFIG_MISSING',
                    description: 'UM Notifications configuration is missing.',
                    use_case: 'NOTIFICATION'
                });
            }
            return;
        }

        if (!isXtremepushConfigValid(config)) {
            return;
        }

        let cancelled = false;

        void initializeNotifications(config).then((manager) => {
            if (cancelled || !manager) {
                return;
            }

            if (userId) {
                void setNotificationsUser(manager, userId);
                return;
            }

            void clearNotificationsUser(manager);
        });

        return () => {
            cancelled = true;
        };
    }, [config, enabled, userId, xpRequested]);
};
